import React, { createContext, useReducer } from "react";
import CreateCaseReducer from "../reducers/create-case.reducer";
import { AIArea } from "../../modules/case/types/AI-areas";
import { AIAlgorithm } from "../../modules/case/types/AI-algorithm";
import { AITechnique } from "../../modules/case/types/AI-techniques";
import { MachineLearningType } from "../../modules/case/types/machine-learning-types";
import { Land } from "../../types/land";
import { Branch } from "../../types/branch";
import { ROLES } from "../../modules/case/types/AI-roles";
import { Operation } from "../../modules/case/types/operations";

interface CreateUseCase {
  role: ROLES | "";

  title: string;
  brief: string;
  description: string;
  scenarioDescription: string;
  version: string;
  link: string;
  riskLevelId: number | null;
  productId: number | null;
  roleId: number | null;
  landId: number | null;
  branchId: number | null;
  areasIds: number[];
  operationsIds: number[];
  algorithmsIds: number[];
  techniquesIds: number[];
  machineLearningIds: number[];
  files: any[];
  users: any[];
}

const createUseCaseInitValue: CreateUseCase = {
  role: "",
  title: "",
  brief: "",
  description: "",
  scenarioDescription: "",
  version: "1",
  link: "",
  riskLevelId: null,
  productId: null,
  roleId: null,
  landId: null,
  branchId: null,
  algorithmsIds: [],
  operationsIds: [],
  areasIds: [],
  techniquesIds: [],
  machineLearningIds: [],
  files: [],
  users: [],
};

interface InitialState {
  useCase: CreateUseCase;
  loading: boolean;
  riskLevels: any[];
  products: any[];
  caseRoles: any[];
  lands: Land[];
  branches: Branch[];
  areas: AIArea[];
  operations: Operation[];
  algorithms: AIAlgorithm[];
  techniques: AITechnique[];
  machineLearningTypes: MachineLearningType[];
  role: ROLES | "";
  setUseCase: (useCase: CreateUseCase) => void;
  setRiskLevels: (riskLevels: any[]) => void;
  setProducts: (products: any[]) => void;
  setCaseRoles: (caseRoles: any[]) => void;
  setLands: (lands: Land[]) => void;
  setBranches: (branches: Branch[]) => void;
  setAreas: (areas: AIArea[]) => void;
  setOperations: (operations: Operation[]) => void;
  setAlgorithms: (algorithms: AIAlgorithm[]) => void;
  setTechniques: (techniques: AITechnique[]) => void;
  setMachineLearningTypes: (
    machineLearningTypes: MachineLearningType[]
  ) => void;
  setRole: (role: ROLES[]) => void;
}

const initialState: InitialState = {
  useCase: { ...createUseCaseInitValue },
  loading: false,
  riskLevels: [],
  products: [],
  caseRoles: [],
  lands: [],
  branches: [],
  areas: [],
  operations: [],
  algorithms: [],
  techniques: [],
  machineLearningTypes: [],
  role: "",
  setUseCase: (useCase: CreateUseCase) => {},
  setRiskLevels: (riskLevels: any[]) => {},
  setProducts: (products: any[]) => {},
  setCaseRoles: (caseRoles: any[]) => {},
  setLands: (lands: Land[]) => {},
  setBranches: (branches: Branch[]) => {},
  setAreas: (areas: AIArea[]) => {},
  setOperations: (operations: Operation[]) => {},
  setAlgorithms: (algorithms: AIAlgorithm[]) => {},
  setTechniques: (techniques: AITechnique[]) => {},
  setMachineLearningTypes: (machineLearningTypes: MachineLearningType[]) => {},
  setRole: (role: ROLES[]) => {},
};

export const CreateUseCaseContext = createContext(initialState);
export const CreateUseCaseProvider = ({ children }: any) => {
  const [state, dispatch] = useReducer(CreateCaseReducer, initialState);

  const setUseCase = (useCase: CreateUseCase) => {
    dispatch({
      type: "SET_USE_CASE",
      payload: { useCase },
    });
  };

  const setLoading = (loading: boolean) => {
    dispatch({
      type: "SET_LOADING",
      payload: { loading },
    });
  };

  const setRiskLevels = (riskLevels: any[]) => {
    dispatch({
      type: "SET_RISK_LEVELS",
      payload: { riskLevels },
    });
  };

  const setProducts = (products: any[]) => {
    dispatch({
      type: "SET_PRODUCTS",
      payload: { products },
    });
  };

  const setCaseRoles = (caseRoles: any[]) => {
    dispatch({
      type: "SET_CASE_ROLES",
      payload: { caseRoles },
    });
  };

  const setLands = (lands: Land[]) => {
    dispatch({
      type: "SET_LANDS",
      payload: { lands },
    });
  };

  const setBranches = (branches: Branch[]) => {
    dispatch({
      type: "SET_BRANCHES",
      payload: { branches },
    });
  };

  const setAreas = (areas: AIArea[]) => {
    dispatch({
      type: "SET_AREAS",
      payload: { areas },
    });
  };

  const setOperations = (operations: Operation[]) => {
    dispatch({
      type: "SET_OPERATIONS",
      payload: { operations },
    });
  };

  const setAlgorithms = (algorithms: AIAlgorithm[]) => {
    dispatch({
      type: "SET_ALGORITHMS",
      payload: { algorithms },
    });
  };

  const setTechniques = (techniques: AITechnique[]) => {
    dispatch({
      type: "SET_TECHNIQUES",
      payload: { techniques },
    });
  };

  const setMachineLearningTypes = (
    machineLearningTypes: MachineLearningType[]
  ) => {
    dispatch({
      type: "SET_MACHINE_LEARNING_TYPES",
      payload: { machineLearningTypes },
    });
  };

  const setRole = (role: ROLES[]) => {
    dispatch({
      type: "SET_ROLE",
      payload: { role },
    });
  };

  return (
    <CreateUseCaseContext.Provider
      value={{
        ...state,
        setUseCase,
        setLoading,
        setProducts,
        setRiskLevels,
        setCaseRoles,
        setLands,
        setBranches,
        setAreas,
        setOperations,
        setAlgorithms,
        setTechniques,
        setMachineLearningTypes,
        setRole,
      }}
    >
      {children}
    </CreateUseCaseContext.Provider>
  );
};
